import React from 'react';

import {
  fetchAuthenticatedGuideMedia,
  isAuthenticatedGuideAssetUrl,
} from './AuthenticatedGuideMedia';
import { sanitizeGuideHtml } from '../utils/sanitizeGuideHtml';

type GuideHtmlRendererProps = {
  className?: string;
  html: string;
};

const AUTHENTICATED_MEDIA_SOURCE_ATTRIBUTE = 'data-authenticated-guide-src';

const prepareAuthenticatedGuideMediaHtml = (html: string) => {
  if (!html || typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;

  const mediaElements = Array.from(
    template.content.querySelectorAll<HTMLImageElement | HTMLVideoElement | HTMLSourceElement>(
      'img[src], video[src], source[src]',
    ),
  );

  mediaElements.forEach(element => {
    const source = element.getAttribute('src') || '';
    if (!isAuthenticatedGuideAssetUrl(source)) return;

    element.setAttribute(AUTHENTICATED_MEDIA_SOURCE_ATTRIBUTE, source);
    element.removeAttribute('src');
  });

  return template.innerHTML;
};

export default function GuideHtmlRenderer({
  className,
  html,
}: GuideHtmlRendererProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const sanitizedHtml = React.useMemo(() => sanitizeGuideHtml(html), [html]);
  const renderHtml = React.useMemo(
    () => prepareAuthenticatedGuideMediaHtml(sanitizedHtml),
    [sanitizedHtml],
  );

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !renderHtml) return undefined;

    let cancelled = false;
    const objectUrls: string[] = [];
    const mediaElements = Array.from(
      container.querySelectorAll<HTMLImageElement | HTMLVideoElement | HTMLSourceElement>(
        `[${AUTHENTICATED_MEDIA_SOURCE_ATTRIBUTE}]`,
      ),
    );

    mediaElements.forEach(element => {
      const source =
        element.getAttribute(AUTHENTICATED_MEDIA_SOURCE_ATTRIBUTE) || '';
      if (!source) return;

      fetchAuthenticatedGuideMedia(source)
        .then(objectUrl => {
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }

          objectUrls.push(objectUrl);
          element.setAttribute('src', objectUrl);
          element.removeAttribute(AUTHENTICATED_MEDIA_SOURCE_ATTRIBUTE);

          if (element instanceof HTMLVideoElement) {
            element.load();
            return;
          }

          const closestVideo = element.closest('video');
          if (closestVideo instanceof HTMLVideoElement) {
            closestVideo.load();
          }
        })
        .catch(() => {
          element.removeAttribute('src');
        });
    });

    return () => {
      cancelled = true;
      objectUrls.forEach(objectUrl => URL.revokeObjectURL(objectUrl));
    };
  }, [renderHtml]);

  if (!renderHtml) return null;

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: renderHtml }}
      ref={containerRef}
    />
  );
}
